import { useCallback, useState } from 'react';
import { useAdiaStore } from '@/lib/store';
import {
  isShareAuthorized,
  sharePrivacyGrantFor,
  sharePrivacyGrantsEqual,
  type SharePrivacyGrant,
} from '@/lib/share-privacy';

export type SharePrivacyBoundary = 'entry' | 'outbound';

export interface SharePrivacyRequest {
  prayerId: string;
  grant: SharePrivacyGrant;
}

export type SharePrivacyConfirmResult =
  | { status: 'authorized'; prayerId: string }
  | { status: 'stale' | 'missing' };

function sameRequest(
  left: SharePrivacyRequest | null,
  right: SharePrivacyRequest,
): boolean {
  return (
    left?.prayerId === right.prayerId &&
    sharePrivacyGrantsEqual(left.grant, right.grant)
  );
}

/**
 * Shared live-store privacy gate for the two share coordinators. Entry
 * confirmation remains below stepped destinations for Back navigation; a
 * separate outbound request can therefore register as a genuinely topmost
 * layer when sync changes privacy while Choice/Text/Mail is already open.
 */
export function useSharePrivacyGate() {
  const [approvedGrant, setApprovedGrant] = useState<SharePrivacyGrant | null>(null);
  const [entryRequest, setEntryRequest] = useState<SharePrivacyRequest | null>(null);
  const [outboundRequest, setOutboundRequest] = useState<SharePrivacyRequest | null>(null);

  const latestPrayer = useCallback((prayerId: string) => {
    return useAdiaStore.getState().prayers.find((prayer) => prayer.id === prayerId) ?? null;
  }, []);

  const authorize = useCallback(
    (prayerId: string, boundary: SharePrivacyBoundary): boolean => {
      const latest = latestPrayer(prayerId);
      if (!latest) return false;
      if (isShareAuthorized(latest, approvedGrant)) return true;

      const request: SharePrivacyRequest = {
        prayerId,
        grant: sharePrivacyGrantFor(latest),
      };
      const setRequest = boundary === 'entry' ? setEntryRequest : setOutboundRequest;
      setRequest((previous) => (sameRequest(previous, request) ? previous : request));
      return false;
    },
    [approvedGrant, latestPrayer],
  );

  const confirm = useCallback(
    (boundary: SharePrivacyBoundary): SharePrivacyConfirmResult => {
      const request = boundary === 'entry' ? entryRequest : outboundRequest;
      if (!request) return { status: 'missing' };

      const latest = latestPrayer(request.prayerId);
      if (!latest) return { status: 'missing' };

      const currentGrant = sharePrivacyGrantFor(latest);
      // A public prayer no longer needs a private grant. For a still-private
      // prayer, a version/privacy mismatch refreshes the warning binding and
      // deliberately consumes this click; the next click is the fresh consent.
      if (latest.isPrivate === true && !sharePrivacyGrantsEqual(request.grant, currentGrant)) {
        const refreshed = { prayerId: latest.id, grant: currentGrant };
        if (boundary === 'entry') setEntryRequest(refreshed);
        else setOutboundRequest(refreshed);
        return { status: 'stale' };
      }

      setApprovedGrant(latest.isPrivate === true ? currentGrant : null);
      return { status: 'authorized', prayerId: latest.id };
    },
    [entryRequest, latestPrayer, outboundRequest],
  );

  const clearEntryRequest = useCallback(() => setEntryRequest(null), []);
  const clearOutboundRequest = useCallback(() => setOutboundRequest(null), []);
  const clearApprovedGrant = useCallback(() => setApprovedGrant(null), []);

  return {
    authorize,
    confirm,
    entryRequest,
    outboundRequest,
    clearEntryRequest,
    clearOutboundRequest,
    clearApprovedGrant,
  };
}
